<template>
  <div class="row justify-content-left mt-5 dp02 p-3">
    <div class="col-lg-12">
      <h2>
        {{ $t('EditGeneCombination.Header') }}
      </h2>
    </div>
    <div class="col-lg-6">
      <ValidationObserver ref="observer"
                          v-slot="{invalid}"
                          tag="form">
        <div class="row my-3">
          <div class="col-lg-3">
            <label for="gender">{{ $t('common.Phenotype') }}:</label>
          </div>
          <div class="col-lg-9">
            <ValidationProvider
              name="phenotype"
              rules="required"
              v-slot="{errors, classes}">
              <b-form-input
                class="myInput"
                v-model="phenotype" />
              <p class="myInputError">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
        </div>
        <div class="row my-3">
          <div class="col-lg-3">
            <label for="gender">{{ $t('common.PhenotypeEn') }}:</label>
          </div>
          <div class="col-lg-9">
            <ValidationProvider
              name="phenotypeEn"
              rules="required"
              v-slot="{errors, classes}">
              <b-form-input
                class="myInput"
                v-model="phenotypeEn" />
              <p class="myInputError">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
        </div>
        <div class="row my-3">
          <div class="col-lg-3">
            <label for="gender">{{ $t('common.Gender') }}:</label>
          </div>
          <div class="col-lg-9">
            <GenderInput
              v-model="gender"
              :with-both="true" />
          </div>
        </div>
        <div class="row my-3">
          <div class="col-lg-3">
            <label for="gender">{{ $t('common.GeneCombination') }}:</label>
          </div>
          <div class="col-lg-9">
            <ValidationProvider
              name="gene combination"
              rules="required|geneCombinationGenesUnique"
              v-slot="{errors, classes}">
              <b-form-textarea
                v-model="geneCombination"
                :class="{...classes, myInput: true}"
                placeholder="Combination"
                no-resize
                rows="7" />
              <p class="myInputError">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('common.Description') }}</h2>
      <ckeditor v-model="description"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('common.DescriptionEn') }}</h2>
      <ckeditor v-model="descriptionEn"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-4 mt-3">
      <h2>{{ $t('common.RiskDefaultTemplate') }}</h2>
      <TemplateRiskInput v-model="risk" />
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('EditGeneCombination.DefaultTemplate') }}</h2>
      <ckeditor v-model="defaultTemplate"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('EditGeneCombination.DefaultTemplateEn') }}</h2>
      <ckeditor v-model="defaultTemplateEn"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-5">
      <b-button
        type="submit"
        class="myButton align-middle"
        @click="editTemplate()">
        {{ $t('EditGeneCombination.EditGeneCombination') }}
      </b-button>
      <b-spinner
        v-if="requestSent"
        variant="primary"
        class="align-middle ml-3"
        label="Spinning" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapterPlugin from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import AutoformatPlugin from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import HeadingPlugin from '@ckeditor/ckeditor5-heading/src/heading';
import FontFamilyPlugin from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSizePlugin from '@ckeditor/ckeditor5-font/src/fontsize';
import FontColorPlugin from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColorPlugin from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import UnderlinePlugin from '@ckeditor/ckeditor5-basic-styles/src/underline';
import StrikethroughPlugin from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import AlignmentPlugin from '@ckeditor/ckeditor5-alignment/src/alignment';
import ListPlugin from '@ckeditor/ckeditor5-list/src/list';
import IndentPlugin from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlockPlugin from '@ckeditor/ckeditor5-indent/src/indentblock';
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import BlockQuotePlugin from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import EasyImagePlugin from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import ImagePlugin from '@ckeditor/ckeditor5-image/src/image';
import ImageCaptionPlugin from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStylePlugin from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbarPlugin from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUploadPlugin from '@ckeditor/ckeditor5-image/src/imageupload';
import TablePlugin from '@ckeditor/ckeditor5-table/src/table';
import TableToolbarPlugin from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import MediaEmbedPlugin from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import HorizontalLinePlugin from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import TemplateRiskInput from '@components/input/TemplateRiskInput';
import Adapter from '@utils/base64uploadadapter';
import GenderInput from '@components/input/GenderInput';

export default {
  name: 'EditGeneCombination',
  components: {
    TemplateRiskInput,
    GenderInput
  },
  data() {
    return {
      editor: DecoupledEditor,
      editorConfig: {
        plugins: [
          EssentialsPlugin,
          AutoformatPlugin,
          HeadingPlugin,
          FontFamilyPlugin,
          FontSizePlugin,
          FontColorPlugin,
          FontBackgroundColorPlugin,
          BoldPlugin,
          ItalicPlugin,
          UnderlinePlugin,
          StrikethroughPlugin,
          AlignmentPlugin,
          ListPlugin,
          IndentPlugin,
          IndentBlockPlugin,
          LinkPlugin,
          BlockQuotePlugin,
          ImagePlugin,
          ImageCaptionPlugin,
          ImageStylePlugin,
          ImageToolbarPlugin,
          EasyImagePlugin,
          ImageUploadPlugin,
          TablePlugin,
          TableToolbarPlugin,
          MediaEmbedPlugin,
          ParagraphPlugin,
          UploadAdapterPlugin,
          HorizontalLinePlugin
        ],
        toolbar: [
          'heading',
          '|',
          'fontFamily',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'alignment',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'outdent',
          'indent',
          '|',
          'link',
          'blockQuote',
          'imageUpload',
          'insertTable',
          'mediaEmbed',
          '|',
          'undo',
          'redo',
          '|',
          'horizontalLine'
        ],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [
            'tiny',
            'small',
            'default',
            'big',
            'huge'
          ]
        },
        image: {
          toolbar: [
            'imageStyle:full',
            'imageStyle:side',
            '|',
            'imageTextAlternative'
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        }
      },
      requestSent: false
    };
  },
  created() {
    this.resetContext();
    this.setGeneCombinationId(this.id);
    this.fetchGeneCombination();
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    description: {
      get() {
        return this.getDescription();
      },
      set(val) {
        this.setDescription(val);
      }
    },
    descriptionEn: {
      get() {
        return this.getDescriptionEn();
      },
      set(val) {
        this.setDescriptionEn(val);
      }
    },
    phenotype: {
      get() {
        return this.getPhenotype();
      },
      set(val) {
        this.setPhenotype(val);
      }
    },
    phenotypeEn: {
      get() {
        return this.getPhenotypeEn();
      },
      set(val) {
        this.setPhenotypeEn(val);
      }
    },
    defaultTemplate: {
      get() {
        return this.getDefaultTemplate();
      },
      set(val) {
        this.setDefaultTemplate(val);
      }
    },
    defaultTemplateEn: {
      get() {
        return this.getDefaultTemplateEn();
      },
      set(val) {
        this.setDefaultTemplateEn(val);
      }
    },
    risk: {
      get() {
        return this.$store.state.editGeneCombination.risk;
      },
      set(val) {
        this.setRisk(val);
      }
    },
    gender: {
      get() {
        return this.$store.state.editGeneCombination.gender;
      },
      set(val) {
        this.setGender(val);
      }
    },
    geneCombination: {
      get() {
        return this.$store.state.editGeneCombination.geneCombination;
      },
      set(val) {
        this.setGeneCombination(val);
      }
    }
  },
  methods: {
    ...mapActions({
      editGeneCombination: 'editGeneCombination/editGeneCombination',
      fetchGeneCombination: 'editGeneCombination/fetchGeneCombination',
      resetContext: 'editGeneCombination/resetContext'
    }),
    ...mapMutations({
      setDescription: 'editGeneCombination/setDescription',
      setDescriptionEn: 'editGeneCombination/setDescriptionEn',
      setGeneCombinationId: 'editGeneCombination/setGeneCombinationId',
      setPhenotype: 'editGeneCombination/setPhenotype',
      setPhenotypeEn: 'editGeneCombination/setPhenotypeEn',
      setDefaultTemplate: 'editGeneCombination/setDefaultTemplate',
      setDefaultTemplateEn: 'editGeneCombination/setDefaultTemplateEn',
      setRisk: 'editGeneCombination/setRisk',
      setGeneCombination: 'editGeneCombination/setGeneCombination',
      setGender: 'editGeneCombination/setGender'
    }),
    ...mapGetters({
      getDescription: 'editGeneCombination/getDescription',
      getDescriptionEn: 'editGeneCombination/getDescriptionEn',
      getPhenotype: 'editGeneCombination/getPhenotype',
      getPhenotypeEn: 'editGeneCombination/getPhenotypeEn',
      getDefaultTemplate: 'editGeneCombination/getDefaultTemplate',
      getDefaultTemplateEn: 'editGeneCombination/getDefaultTemplateEn'
    }),
    onReady(editor) {
      // Insert the toolbar before the editable area.
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement()
        );
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new Adapter(loader);
    },
    async editTemplate() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.requestSent = true;
        this.editGeneCombination()
          .then(() => {
            this.requestSent = false;
            this.resetContext();
            this.$root.$bvToast.toast('Fenotyp úspěšně upraven', {
              title: 'Úspěšně upraveno',
              variant: 'success',
              toaster: 'b-toaster-top-right'
            });
            this.$router.replace({ name: 'admin.templates.genecombinations.view' });
          })
          .catch(() => {
            this.requestSent = false;
            this.$root.$bvToast.toast(this.$t('EditGeneCombination.EditToast.Body'), {
              title: this.$t('EditGeneCombination.EditToast.Title'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
